import { Box, useMantineTheme } from '@mantine/core';
import { type ComponentProps, forwardRef, type ReactNode, useId } from 'react';
import { useTranslation } from 'react-i18next';
import ReactPhoneInput from 'react-phone-input-2';
import pl from 'react-phone-input-2/lang/pl.json';
import { FieldErrorMessage } from 'shared/ui/FieldErrorMessage';
import { getInputStyles } from 'theme/components/Input';
import {
  getDescriptionStyles,
  getLabelStyles,
} from 'theme/components/InputWrapper';

import { ChevronDownIcon } from './assets';

const SELECTED_FLAG_WIDTH = 46;

export type Props = {
  label?: ReactNode;
  description?: ReactNode;
  error?: ReactNode;
  withErrorWrapperAlwaysEnabled?: boolean;
  inputProps?: object;
} & ComponentProps<typeof ReactPhoneInput>;

export const PhoneInput = forwardRef<HTMLInputElement, Props>(
  (
    {
      label,
      description,
      error,
      withErrorWrapperAlwaysEnabled = true,
      inputProps = {},
      disabled,
      disableDropdown,
      ...reactPhoneInputProps
    },
    ref,
  ) => {
    const {
      i18n: { language },
    } = useTranslation();
    const theme = useMantineTheme();

    const inputId = useId();

    return (
      <div>
        {label && (
          <Box
            sx={theme => getLabelStyles(theme)}
            component="label"
            htmlFor={inputId}
          >
            {label}
          </Box>
        )}
        <Box
          sx={t => {
            const inputFocusRingStyles = t.focusRingStyles.inputStyles(t);
            const inputStyles = getInputStyles(t);

            return {
              '&&&': {
                fontFamily: 'inherit',
              },
              '&&& .form-control': {
                ...{
                  fontFamily: 'inherit',
                  ...inputStyles,
                },
                width: 'calc(100% - 1px)',
                left: 1,
                paddingLeft: t.other.spacing(7),
                borderRadius: t.radius.sm,
                '&:disabled': {
                  backgroundColor: t.colors.gray[1],
                  borderColor: t.colors.gray[3],
                },
                '&: focus-visible': {
                  ...inputFocusRingStyles,
                  '+ .flag-dropdown': {
                    borderRight: `1px solid ${inputFocusRingStyles.borderColor}`,
                  },
                },
              },
              '&&& .flag-dropdown': {
                borderColor: inputStyles.borderColor,
                borderRadius: `${t.radius.sm}px 0 0 ${t.radius.sm}px`,
                backgroundColor: t.white,
              },
              '&&& .selected-flag': {
                width: SELECTED_FLAG_WIDTH,
                '&: focus-visible': {
                  height: 'calc(100% + 2px)',
                  top: -1,
                  border: `1px solid ${inputFocusRingStyles.borderColor}`,
                },
              },
              '&&& .arrow': {
                width: 16,
                height: 16,
                margin: 'auto',
                top: -2,
                bottom: 0,
                background: `url("${ChevronDownIcon}") no-repeat center`,
                border: 'none',
                transition: `transform ${t.other.animationsTime} ease`,
                transform: 'rotate(0)',
                '&.up': {
                  border: 'none',
                  transition: `transform ${t.other.animationsTime} ease 0s`,
                  transform: 'rotate(180deg)',
                },
              },
              '.country': {
                fontSize: t.fontSizes.sm,
              },
              '.flag': {
                position: 'relative',
                top: 2,
              },
            };
          }}
          component={ReactPhoneInput}
          country="pl"
          localization={language === 'pl' ? pl : undefined}
          placeholder="+48 123 456 789"
          inputProps={{ id: inputId, ref, ...inputProps }}
          disabled={disabled}
          disableDropdown={disableDropdown ?? disabled}
          buttonStyle={
            disabled
              ? {
                  borderColor: theme.colors.gray[3],
                  backgroundColor: theme.colors.gray[1],
                  cursor: 'default',
                  pointerEvents: 'none',
                }
              : undefined
          }
          {...reactPhoneInputProps}
        />
        {description && (
          <Box
            sx={theme => ({
              ...getDescriptionStyles(theme),
              margin: theme.other.spacing(0.5, 0, 0),
            })}
            component="p"
          >
            {description}
          </Box>
        )}
        {(error || withErrorWrapperAlwaysEnabled) && (
          <FieldErrorMessage>{error}</FieldErrorMessage>
        )}
      </div>
    );
  },
);
